import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import SubmitBtn from 'src/component/ActionBtn/SubmitBtn'
import CostTag from 'src/component/CostTag'
import MedicinesSubmitionPopup from 'src/component/MedicinesPopup'
import PopupContainer from 'src/component/popup/PopupContainer'
import { useMutation } from 'src/hooks/useMutation'
import { GetRequestMedicines, SubmitRequest } from 'src/requests/Requests'
import { SubmitChangeRequestMedicinesRequest } from 'src/requests/Shared'
import { errorToaster, getHospitalConfiguralion } from 'src/utils'
import EligibilityInfo from './EligibilityInfo'
import SummaryRequestMedicines from 'src/component/MedicinesPopup/SummaryRequestMedicines'
import { PatientData } from 'src/types/patientData'
import { usePrintImages } from 'src/hooks/usePrintImages'
import { ALLOWED_MONTHS_TO_COVER } from 'src/common/constants'
import BatchDispensePopup from 'src/component/popup/BatchDispensePopup/BatchDispensePopup'
import PrintRequest from 'src/component/PrintedPDF/PrintRequestTable'
import { privilegesContext } from 'src/context/privileges'

interface IAlterMedicinePopup {
    recordID: number | null
    modalID: any
    hospitalId: number | null
    remainingAmount: number | null
    mothlyAmount: number | null
    decisionId?: number | null
    patientData: PatientData | null
    setRecordID: (value: number | null) => void
}

const MedicinesPopup: FC<IAlterMedicinePopup> = ({ modalID, recordID, setRecordID, hospitalId, remainingAmount, mothlyAmount, decisionId, patientData }) => {
    const { t } = useTranslation()
    const { state } = useLocation()
    const { hospitalCofigurations } = useContext(privilegesContext)

    const { mutate: submit, isPending: isLoadingSubmit } = useMutation()
    const { mutate: change, isPending: isLoadingChange } = useMutation()

    const [totalCost, setTotalCost] = useState(0)
    const [medicineList, setMedicineList] = useState([])
    const [coveredMonth, setCoveredMonth] = useState(ALLOWED_MONTHS_TO_COVER[0].value)
    const [requestIDToViewBatches, setRequestIDToViewBatches] = useState<number | null>(null)
    const [requestIdForPrint, setRequestIdForPrint] = useState<number | null>(null)

    const summaryRequestMedicinesModal = useRef<any>("")
    const printRequestMedicines = useRef<HTMLElement | null>(null);
    const printRequestIsNeedCoolingMedicines = useRef<HTMLElement | null>(null);
    const batchDispensePopupRef = useRef<HTMLDivElement>(null);

    const { handlePrint } = usePrintImages({ title: "request-print" })

    const isAllowToDispenseMedicinesForMoreThanOneMonth = useMemo(() =>
        getHospitalConfiguralion(hospitalCofigurations, 'AllowToDispenseMedicinesForMoreThanOneMonthInOneRequest'), [hospitalCofigurations])

    //@ts-ignore
    useEffect(() => {
        //@ts-ignore
        let tempActiveTotalCost = !medicineList?.length ? 0 : medicineList.filter(item => item.isSelected && !item.fromDonationsStock).map(item => Number(item?.cost || 0)).reduce((nxt, cur) => nxt + cur, 0)
        //@ts-ignore
        let tempAltTotalCost = !medicineList?.length ? 0 : medicineList.filter(item => !item.isSelected && item.altMedicine && !item?.isDefault && !item.fromDonationsStock).map(item => Number(item?.altPrice || 0)).reduce((nxt, cur) => nxt + cur, 0)
        setTotalCost((tempActiveTotalCost + tempAltTotalCost) * coveredMonth)
    }, [medicineList, coveredMonth])
    const handleCreateRequest = () => {

        if (totalCost > (remainingAmount || 0)) return errorToaster('higher-sum-alter')
        if (!coveredMonth && isAllowToDispenseMedicinesForMoreThanOneMonth?.isAllowed) return

        let RequestBody = {
            pharmacyId: state?.pharmName?.value,
            requestId: recordID,
            requestMedicines: getRequestList(),
            alternativeMedicines: getAlternativeMedicines(),
            ...(isAllowToDispenseMedicinesForMoreThanOneMonth?.isAllowed && { coveredMonths: coveredMonth })
        }
        let requestSetup = {
            apiMethod: SubmitRequest,
            id: null,
            body: RequestBody,
            actionType: "post",
            onSuccess: () => {
                handleResetSubmitRequest();
                setRequestIDToViewBatches(recordID);
            },
            customActionTitle: 'submit-order-confirmation'
        }
        //@ts-ignore
        submit(requestSetup)
    }

    const getRequestList = () => {
        let requestBodyMedicine = medicineList.map((item: any) => (
            {
                "requestMedicineId": item?.reqMedId,
                "isAvailable": item?.isSelected,
                "fromDonationsStock": item.fromDonationsStock || false,
                "isModel56": item?.isModel56,
            }
        ))
        return requestBodyMedicine
    }
    const getAlternativeMedicines = () => {
        //@ts-ignore
        let CheckedList = medicineList.filter(item => !!item?.isSelected).map(item => item.reqMedId)

        //@ts-ignore
        let AltList = medicineList.filter(item => !item?.isSelected && item?.altMedicine?.value && !item?.isDefault)

        if (!CheckedList?.length && !AltList?.length) {
            errorToaster("please-select-one-item")
            return false
        }

        let mappedListForRequest = AltList.map((item: any) => {
            return ({
                altToRequestMedicineId: item?.reqMedId,
                fromDonationsStock: item.fromDonationsStock || false,
                AltMedicine: {
                    medicineId: item?.altMedicine?.value,
                    dosePerDay: item?.doseAmount,
                    doseCountPerTime: item?.doseRepeat,
                    doseTypeId: item?.userDoseRepeate?.value,
                    duration: item?.duration,
                    durationType: item?.durationType?.value,
                    directAmount: item?.altMedicine?.allowDirectAmount ? item?.directAmount : null,
                    isEssential: item?.isEssential || false,
                    // isCustomAmount: item?.isCustomAmount || false
                }
            })
        }) || []

        return mappedListForRequest
    }


    const changeRequest = () => {
        //@ts-ignore
        let CheckedList = medicineList.filter(item => item?.isSelected).map(item => item?.reqMedId)

        let RequestBody = {
            requestId: recordID,
            needToChangeMedicinesIds: CheckedList
        }

        let requestSetup = {
            apiMethod: SubmitChangeRequestMedicinesRequest,
            id: null,
            body: RequestBody,
            actionType: "post",
            name: 'changeRequest',
            onSuccess: handleResetChangeRequest,
        }
        //@ts-ignore
        change(requestSetup)

    }

    const handleResetSubmitRequest = () => {
        summaryRequestMedicinesModal.current.click()
        setMedicineList([])
        setCoveredMonth(ALLOWED_MONTHS_TO_COVER[0].value)
    }

    const handleResetChangeRequest = () => {
        setRecordID(null)
        modalID.current.click()
        setMedicineList([])
    }

    const selectedMedicinesSummaryList = useMemo(() => {
        return medicineList.filter((item: any) => item.isSelected || (item.altMedicine?.value && !item.isSelected && !item.isDefault))
    }, [medicineList, coveredMonth])

    useEffect(() => {
        if (!requestIDToViewBatches) return
        batchDispensePopupRef?.current?.click();
    }, [requestIDToViewBatches])

    return (
        <>
            <BatchDispensePopup
                modalId={batchDispensePopupRef}
                recordId={requestIDToViewBatches}
                handlePrint={() => setRequestIdForPrint(requestIDToViewBatches)}
            />
            <PrintRequest
                handlePrint={handlePrint}
                printRefs={[printRequestMedicines, printRequestIsNeedCoolingMedicines]}
                selectedPrintRequestId={requestIdForPrint}
                setSelectedPrintRequestId={setRequestIdForPrint}
            />

            <SummaryRequestMedicines
                selectedMedicinesSummaryList={selectedMedicinesSummaryList}
                patientData={patientData}
                modalID={summaryRequestMedicinesModal}
                handleCreateRequest={handleCreateRequest}
                medicinesHeaderPrices={{ medicinePrice: totalCost, remainingAmount: remainingAmount, allowedMonthlyAmount: mothlyAmount }}
                isLoadingSubmit={isLoadingSubmit}
                prevModalID={modalID}
                coveredMonths={coveredMonth}
            />

            <PopupContainer
                Name={"request-medicine-order"}
                modalName="ChangeMedicine"
                modalID={modalID}
                resetAllFields={() => { setMedicineList(prev => []); setRecordID(null); setCoveredMonth(ALLOWED_MONTHS_TO_COVER[0].value) }}
                AdditionalTitle={` ${t('forOrderNumber', { orderNumber: recordID })}`}>
                <EligibilityInfo recordID={recordID} />
                <div className='row'>
                    <CostTag amount={totalCost} title={"total-cost"} />
                    <CostTag amount={remainingAmount} title={"remaining-amount"} />
                    <CostTag amount={mothlyAmount} title={"monthly-amount"} />
                </div>

                <MedicinesSubmitionPopup
                    isManual
                    // isCustomAmount
                    recordID={recordID}
                    hospitalId={hospitalId}
                    medicineList={medicineList}
                    setMedicineList={setMedicineList}
                    ApiMethod={GetRequestMedicines}
                    isParamsValid={!!state?.pharmName?.value && !!recordID}
                    depArrItems={[recordID, state?.pharmId]}
                    params={[recordID, hospitalId, state?.pharmName?.value]}
                    defaultValue={[]}
                    hasEssentialCheckbox={true}
                    decisionId={decisionId}
                    setCoveredMonth={setCoveredMonth}
                    coveredMonth={coveredMonth}
                />

                <div className='row'>
                    <div className=' col-sm-6'>
                        <SubmitBtn
                            label={t('proccessed-to-order-summary')}
                            onSubmit={() => summaryRequestMedicinesModal.current.click()}
                            loading={isLoadingSubmit}
                            disabled={!selectedMedicinesSummaryList || selectedMedicinesSummaryList.length === 0} />
                    </div>
                    <div className=' col-sm-6'>
                        <SubmitBtn
                            label={t('change-request')}
                            onSubmit={changeRequest}
                            loading={isLoadingChange}
                            className={'change-request'}
                            disabled={!selectedMedicinesSummaryList || selectedMedicinesSummaryList.length === 0} />
                    </div>
                </div>

            </PopupContainer >
        </>
    )
}

export default MedicinesPopup